import { useCardContext } from '@hts/entities/Card/libs';
import styles from '@hts/entities/Card/styles/InfoContent/AddMusic/SelectMusicPopup.module.scss';
import { useAudioPlayerStore } from '@hts/shared/libs/zustand';
import { ICardAudioItemInput } from '@hts/shared/types';
import { HtsButtonIcon, HtslIcon, HtsTypography } from '@hts/shared/ui';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { MusicList } from './MusicList';

interface ISelectMusicPopupProps {
  resendCard: boolean;
  open: boolean;
  checkedSong: ICardAudioItemInput | null;
  setCheckedSong: (song: ICardAudioItemInput | null) => void;
  onClose: () => void;
}

export const SelectMusicPopup: React.FC<ISelectMusicPopupProps> = memo(
  ({ open, resendCard, checkedSong, setCheckedSong, onClose }) => {
    const card = useCardContext((state) => state.card);
    const musicList = useCardContext((state) => state.musicList);
    const draftCheckedMusicId = useCardContext((state) => state.draftCheckedMusicId);
    const setDraftCheckedMusicId = useCardContext((state) => state.setDraftCheckedMusicId);
    const setCard = useCardContext((state) => state.setCard);

    const { setActiveSong } = useAudioPlayerStore();

    const scrollContainerRef = useRef<HTMLDivElement | null>(null);

    const checkedMusicId = draftCheckedMusicId || card?.orderAudioId;

    const foundSong = useMemo(() => {
      if (!checkedMusicId || !musicList) return null;

      let song = musicList.cardAudios.find(({ id }) => id === checkedMusicId) ?? null;

      if (!song) {
        for (const folder of musicList.cardFolders) {
          const found = folder.media.find(({ id }) => id === checkedMusicId);

          if (found) {
            song = found;
            break;
          }
        }
      }

      return song;
    }, [checkedMusicId, musicList]);

    useEffect(() => {
      if (foundSong) {
        setCheckedSong(foundSong);
        setDraftCheckedMusicId?.(null);
      }
    }, [foundSong]);

    useEffect(() => {
      if (!open || !checkedSong || !scrollContainerRef.current) return;

      const element = scrollContainerRef.current.querySelector<HTMLLIElement>(
        `#song-${checkedSong.id}`
      );

      if (element) {
        requestAnimationFrame(() => {
          const containerBounds = scrollContainerRef.current?.getBoundingClientRect();
          const elementBounds = element.getBoundingClientRect();

          if (containerBounds) {
            const offset =
              elementBounds.top -
              containerBounds.top +
              (scrollContainerRef.current?.scrollTop || 0);

            scrollContainerRef.current?.scrollTo({ top: offset - 20, behavior: 'smooth' });
          }
        });
      }
    }, [open, checkedSong]);

    const setCheckedMusic = useCallback((musicId?: number) => {
      setCard((prev) => prev && { ...prev, audio_id: musicId ? String(musicId) : undefined });
    }, []);

    const chooseSong = useCallback((song: ICardAudioItemInput | null) => {
      setCheckedSong(song);
      setCheckedMusic?.(song?.id);
    }, []);

    const handleClose = useCallback(() => {
      setActiveSong(undefined);
      onClose();
    }, [onClose]);

    return (
      <div
        className={styles.container}
        style={{
          gridTemplateRows: open ? '1fr' : '0fr',
          paddingBlock: open ? '25px 26px' : 0,
          opacity: open ? 1 : 0,
        }}
      >
        <div className={styles.contentContainer}>
          <div className={styles.headContainer}>
            <HtsTypography fontWeight="600" style={{ wordBreak: 'break-word' }}>
              Select A Song
            </HtsTypography>

            <HtsButtonIcon className={styles.closeButton} onClick={handleClose}>
              <HtslIcon icon="cancel" color="primaryDark" style={{ fontSize: 20 }} />
            </HtsButtonIcon>
          </div>

          <MusicList
            cardAudios={musicList?.cardAudios}
            cardFolders={musicList?.cardFolders}
            checkedSong={checkedSong}
            alwaysCheckedOne={resendCard}
            chooseSong={chooseSong}
          />
        </div>
      </div>
    );
  }
);
